import * as React from 'react';
import { Popover as Popover$1, FocusTrap } from '@headlessui/react';
import { Float } from '@headlessui-float/react';
import clsx from 'clsx';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Popover-module_popoverContent__fFPs1{background-color:var(--color-white);border-radius:.375rem;box-shadow:0 0 0 0 #fff,0 0 0 1px rgba(0,0,0,.05),0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -2px rgba(0,0,0,.05);outline:2px solid transparent;outline-offset:2px;transform-origin:top right;z-index:10}.Popover-module_popoverTrigger__8wyAu{position:relative}.Popover-module_popoverArrow__e-8Kq{background:var(--color-white);box-shadow:0 0 0 0 #fff,0 0 0 1px rgba(0,0,0,.05),0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -2px rgba(0,0,0,.05);height:20px;position:absolute;transform:rotate(45deg);width:21px}.Popover-module_popoverContentContainerWithArrow__qndRd{background:var(--color-white);border-radius:.375rem;padding:.75rem;position:relative}.Popover-module_popoverContentContainer__Y-IRH{padding:.25rem .5rem}.Popover-module_transitionEnter__e-Aqs{transition-duration:.1s;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,-webkit-backdrop-filter;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter,-webkit-backdrop-filter;transition-timing-function:cubic-bezier(0,0,.2,1)}.Popover-module_transitionEnterFrom__7OvwY{opacity:0;transform:scale(.95)}.Popover-module_transitionEnterTo__SifgM{opacity:1;transform:scale(1)}.Popover-module_transitionLeave__Zq8-l{transition-duration:75ms;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,-webkit-backdrop-filter;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter,-webkit-backdrop-filter;transition-timing-function:cubic-bezier(.4,0,1,1)}.Popover-module_transitionLeaveFrom__l5pgc{opacity:1;transform:scale(1)}.Popover-module_transitionLeaveTo__mqPIO{opacity:0;transform:scale(.95)}";
var styles = {"popoverContent":"Popover-module_popoverContent__fFPs1","popoverTrigger":"Popover-module_popoverTrigger__8wyAu","popoverArrow":"Popover-module_popoverArrow__e-8Kq","popoverContentContainerWithArrow":"Popover-module_popoverContentContainerWithArrow__qndRd","popoverContentContainer":"Popover-module_popoverContentContainer__Y-IRH","transitionEnter":"Popover-module_transitionEnter__e-Aqs","transitionEnterFrom":"Popover-module_transitionEnterFrom__7OvwY","transitionEnterTo":"Popover-module_transitionEnterTo__SifgM","transitionLeave":"Popover-module_transitionLeave__Zq8-l","transitionLeaveFrom":"Popover-module_transitionLeaveFrom__l5pgc","transitionLeaveTo":"Popover-module_transitionLeaveTo__mqPIO"};
styleInject(css_248z);

/**
 * Creates a named context, provider, and hook.
 *
 * @param options create context options
 */
function createContext(options) {
    if (options === void 0) { options = {}; }
    var _a = options.strict, strict = _a === void 0 ? true : _a, _b = options.errorMessage, errorMessage = _b === void 0 ? 'useContext: `context` is undefined. Seems you forgot to wrap component within the Provider' : _b, name = options.name;
    var Context = React.createContext(undefined);
    Context.displayName = name;
    function useContext() {
        var _a;
        var context = React.useContext(Context);
        if (!context && strict) {
            var error = new Error(errorMessage);
            error.name = 'ContextError';
            (_a = Error.captureStackTrace) === null || _a === void 0 ? void 0 : _a.call(Error, error, useContext);
            throw error;
        }
        return context;
    }
    return [
        Context.Provider,
        useContext,
        Context,
    ];
}

var _a;
var PopoverContextProvider = (_a = createContext(), _a[0]), usePopoverContext = _a[1];

var DESCRIPTION_NAME$1 = 'PopoverTrigger';
var PopoverTrigger = React.forwardRef(function (_a, ref) {
    var children = _a.children, className = _a.className, props = __rest(_a, ["children", "className"]);
    var classes = clsx(styles.popoverTrigger, className);
    return (React.createElement(Popover$1.Button, __assign({ className: classes }, props, { ref: ref }), children));
});
PopoverTrigger.displayName = DESCRIPTION_NAME$1;

var DESCRIPTION_NAME = 'PopoverContent';
var PopoverContent = React.forwardRef(function (_a, ref) {
    var className = _a.className, children = _a.children, _b = _a.focus, focus = _b === void 0 ? true : _b, _c = _a.containerStyles, containerStyles = _c === void 0 ? {} : _c, props = __rest(_a, ["className", "children", "focus", "containerStyles"]);
    var arrow = usePopoverContext().arrow;
    var classes = clsx(styles.popoverContent, className);
    return (React.createElement(Popover$1.Panel, __assign({ focus: focus, className: classes }, props, { ref: ref, static: true }), function (_a) {
        var close = _a.close, open = _a.open;
        var childrenComponent = typeof children === 'function'
            ? children({ close: close, open: open })
            : children;
        // 30: enable all features of focus trap including auto focus
        // 4: only enable tab lock
        // check the headlessui code for more details (not documented in the headlessui docs)
        var isEnableAutoFocus = focus ? 30 : 4;
        return (React.createElement(FocusTrap, { features: isEnableAutoFocus, initialFocus: props.initialFocus },
            arrow && (React.createElement(Float.Arrow, { "data-testid": "popover-arrow", className: styles.popoverArrow })),
            React.createElement("div", { className: clsx(styles.popoverContentContainer, arrow && styles.popoverContentContainerWithArrow), style: containerStyles }, childrenComponent)));
    }));
});
PopoverContent.displayName = DESCRIPTION_NAME;

function OpenListener(_a) {
    var open = _a.open;
    var setIsOpen = usePopoverContext().setIsOpen;
    React.useEffect(function () {
        setIsOpen(open);
    }, [open, setIsOpen]);
    return React.createElement(React.Fragment, null);
}
var Popover = function (_a) {
    var _b, _c;
    var children = _a.children, _d = _a.placement, placement = _d === void 0 ? 'bottom-end' : _d, _e = _a.trigger, trigger = _e === void 0 ? 'click' : _e, _f = _a.arrow, arrow = _f === void 0 ? false : _f, _g = _a.offset, offset = _g === void 0 ? [4, 0] : _g, onClose = _a.onClose, onOpen = _a.onOpen, _h = _a.nested, nested = _h === void 0 ? false : _h, _j = _a.floatingAs, floatingAs = _j === void 0 ? 'div' : _j, props = __rest(_a, ["children", "placement", "trigger", "arrow", "offset", "onClose", "onOpen", "nested", "floatingAs"]);
    var isMounted = React.useRef(false);
    var _k = React.useState(false), isOpen = _k[0], setIsOpen = _k[1];
    var floatOffset = {
        mainAxis: (_b = offset === null || offset === void 0 ? void 0 : offset[0]) !== null && _b !== void 0 ? _b : 0,
        crossAxis: (_c = offset === null || offset === void 0 ? void 0 : offset[1]) !== null && _c !== void 0 ? _c : 0,
    };
    React.useEffect(function () {
        // to prevent the first render from triggering the onOpen/onClose callback
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        var handleFloatUpdate = function (isOpen) {
            if (isOpen)
                onOpen === null || onOpen === void 0 ? void 0 : onOpen();
            else
                onClose === null || onClose === void 0 ? void 0 : onClose();
        };
        handleFloatUpdate(isOpen);
    }, [isOpen, onClose, onOpen]);
    var PopoverTrigger = children.find(function (child) {
        var _a;
        return ((_a = child === null || child === void 0 ? void 0 : child.type) === null || _a === void 0 ? void 0 : _a.displayName) === 'PopoverTrigger';
    });
    var PopoverContent = children.find(function (child) {
        var _a;
        return ((_a = child === null || child === void 0 ? void 0 : child.type) === null || _a === void 0 ? void 0 : _a.displayName) === 'PopoverContent';
    });
    var isUsePortal = nested === false;
    var isTriggerHover = trigger === 'hover';
    return (React.createElement(PopoverContextProvider, { value: { isOpen: isOpen, setIsOpen: setIsOpen, arrow: arrow } },
        React.createElement("div", __assign({}, props),
            React.createElement(Popover$1, { onMouseEnter: function () { return isTriggerHover && setIsOpen(true); }, onMouseLeave: function () { return isTriggerHover && setIsOpen(false); } }, function (_a) {
                var open = _a.open;
                return (React.createElement(React.Fragment, null,
                    React.createElement(OpenListener, { open: open }),
                    React.createElement(Float, { enter: styles.transitionEnter, enterFrom: styles.transitionEnterFrom, enterTo: styles.transitionEnterTo, leave: styles.transitionLeave, leaveFrom: styles.transitionLeaveFrom, leaveTo: styles.transitionLeaveTo, show: open || isOpen, offset: floatOffset, placement: placement, portal: isUsePortal, arrow: arrow, floatingAs: floatingAs, flip: true }, PopoverTrigger !== null && PopoverTrigger !== void 0 ? PopoverTrigger : React.createElement(React.Fragment, null), PopoverContent !== null && PopoverContent !== void 0 ? PopoverContent : React.createElement(React.Fragment, null))));
            }))));
};

export { Popover, PopoverContent, PopoverTrigger };
